import { useState, useEffect, useCallback } from "react";
import Select, { MultiValue, StylesConfig, components } from "react-select";
import { Option } from "../../interface/Option";
import { SpecialtyAxiosInstance } from "../../Network/SpecialtyAxiosInstance";

interface ListOfSpecialtyProps {
  onChange: (selected: any) => void;
  clear?: boolean;
  defaultValue?: any;
  value?: any;
  qaIdPrefix?: string;
  [key: string]: any;
}

const DEFAULT_QA_PREFIX = "list-of-specialty";

const qaSafe = (value: string | number | undefined | null) =>
  String(value ?? "unknown")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

const buildQaId = (prefix: string, suffix: string | number) =>
  `${qaSafe(prefix)}-${qaSafe(suffix)}`;

const qaAttrs = (prefix: string, suffix: string | number) => {
  const id = buildQaId(prefix, suffix);
  return {
    id,
    "data-testid": id,
    "data-qa": id,
  };
};

const ListOfSpecialty = ({
  onChange,
  clear,
  defaultValue,
  value,
  qaIdPrefix = DEFAULT_QA_PREFIX,
}: ListOfSpecialtyProps) => {
  const [options, setOptions] = useState<Option[]>([]);
  const [selectedOptions, setSelectedOptions] = useState<Option[]>([]);
  const [loading, setLoading] = useState(false);
  const [inputValue, setInputValue] = useState<string>("");
  const [allOptions, setAllOptions] = useState<Option[]>([]);

  const selectQaId = buildQaId(qaIdPrefix, "select");
  const inputQaId = buildQaId(qaIdPrefix, "input");

  const loadAllOptions = useCallback(async () => {
    setLoading(true);
    try {
      const response = await SpecialtyAxiosInstance.get("getSpecialty", {
        params: { active: "true" },
      });
      const data = response.data?.activities || [];

      const formattedOptions: Option[] = data.map((item: any) => ({
        value: item.id,
        label: `(${item.id}) ${item.name}`,
      }));

      setAllOptions(formattedOptions);
      setOptions(formattedOptions);
    } catch (error) {
      console.error("Error loading specialties:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAllOptions();
  }, [loadAllOptions]);

  // فلتر محلي
  useEffect(() => {
    if (inputValue.trim().length === 0) {
      setOptions(allOptions);
      return;
    }
    if (inputValue.trim().length < 2) {
      setOptions([]);
      return;
    }
    const filtered = allOptions.filter((option) =>
      option.label.toLowerCase().includes(inputValue.toLowerCase())
    );
    setOptions(filtered);
  }, [inputValue, allOptions]);

  const handleChange = (selected: MultiValue<Option>) => {
    setSelectedOptions(selected as Option[]);
    onChange(selected as Option[]);
  };

  useEffect(() => {
    if (clear) setSelectedOptions([]);
  }, [clear]);

  useEffect(() => {
    const incoming = value ?? defaultValue;
    if (incoming !== undefined) setSelectedOptions(incoming);
  }, [value, defaultValue]);

  const customStyles: StylesConfig<Option, true> = {
    multiValue: (styles) => ({
      ...styles,
      backgroundColor: "#007BFF",
      color: "#FFFFFF",
      borderRadius: "5px",
    }),
    multiValueLabel: (styles) => ({ ...styles, color: "#FFFFFF" }),
    multiValueRemove: (styles) => ({
      ...styles,
      color: "#FFFFFF",
      ":hover": { backgroundColor: "#0056b3", color: "#FFFFFF" },
    }),
    option: (styles, { isSelected }) => ({
      ...styles,
      backgroundColor: isSelected ? "#007BFF" : "#FFFFFF",
      color: isSelected ? "#FFFFFF" : "#000000",
      ":hover": { backgroundColor: "#0056b3", color: "#FFFFFF" },
    }),
  };

  const Menu = (props: any) => (
    <div {...qaAttrs(qaIdPrefix, "menu")}>
      <components.Menu {...props} />
    </div>
  );

  const OptionComponent = (props: any) => {
    const optionId = qaSafe(String(props.data?.value ?? "unknown"));
    return (
      <div
        {...qaAttrs(qaIdPrefix, `option-${optionId}`)}
        data-option-label={props.data?.label ?? ""}
        data-option-value={props.data?.value ?? ""}
      >
        <components.Option {...props} />
      </div>
    );
  };

  const MultiValueComponent = (props: any) => {
    const selectedId = qaSafe(String(props.data?.value ?? "unknown"));
    return (
      <div
        {...qaAttrs(qaIdPrefix, `selected-${selectedId}`)}
        data-selected-label={props.data?.label ?? ""}
        data-selected-value={props.data?.value ?? ""}
      >
        <components.MultiValue {...props} />
      </div>
    );
  };

  return (
    <div {...qaAttrs(qaIdPrefix, "wrapper")}>
      <Select
        isMulti
        inputId={inputQaId}
        instanceId={selectQaId}
        name={selectQaId}
        aria-label="Specialty selector"
        data-testid={selectQaId}
        data-qa={selectQaId}
        value={selectedOptions}
        onChange={handleChange}
        options={options}
        isLoading={loading}
        onInputChange={setInputValue}
        placeholder="Type to search or select..."
        styles={customStyles}
        components={{
          Menu,
          Option: OptionComponent,
          MultiValue: MultiValueComponent,
        }}
      />

      <span
        {...qaAttrs(qaIdPrefix, "loading-state")}
        data-loading={loading ? "true" : "false"}
        style={{ display: "none" }}
      >
        {loading ? "Loading" : "Idle"}
      </span>

      <span
        {...qaAttrs(qaIdPrefix, "selected-count")}
        data-count={selectedOptions.length}
        style={{ display: "none" }}
      >
        {selectedOptions.length}
      </span>

      <span
        {...qaAttrs(qaIdPrefix, "options-count")}
        data-count={options.length}
        style={{ display: "none" }}
      >
        {options.length}
      </span>

      <span
        {...qaAttrs(qaIdPrefix, "input-value")}
        data-value={inputValue}
        style={{ display: "none" }}
      >
        {inputValue}
      </span>
    </div>
  );
};

export default ListOfSpecialty;
